import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Lock, Save, Download, History } from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';

export default function Profil() {
  const { t } = useLanguage();
  const [me, setMe] = useState(null);
  const [logs, setLogs] = useState([]);
  const [pwd, setPwd] = useState({ old_password: '', new_password: '', confirm: '' });

  const load = async () => {
    const r = await api.get('/client/me');
    setMe(r.data.user);
    const lh = await api.get('/client/login-history');
    setLogs(lh.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put('/client/profile', {
        civilite: me.civilite, nom: me.nom, prenom: me.prenom, telephone: me.telephone,
        adresse_numero: me.adresse_numero, adresse_rue: me.adresse_rue,
        adresse_code_postal: me.adresse_code_postal, adresse_ville: me.adresse_ville, adresse_pays: me.adresse_pays,
      });
      toast.success(t('profile.saved'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('common.error')); }
  };
  const change = async () => {
    if (pwd.new_password !== pwd.confirm) { toast.error(t('auth.pwMismatch')); return; }
    try {
      await api.post('/auth/change-password', { old_password: pwd.old_password, new_password: pwd.new_password });
      toast.success(t('common.success'));
      setPwd({ old_password: '', new_password: '', confirm: '' });
    } catch (e) { toast.error(e?.response?.data?.detail || t('common.error')); }
  };
  const downloadRib = async () => {
    try {
      const res = await api.get('/client/rib.pdf', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `RIB_BFI_${me.nom}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(t('common.error')); }
  };

  if (!me) return <div>{t('common.loading')}</div>;
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t('profile.title')}</h1>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-semibold text-lg mb-4">{t('profile.personalInfo')}</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>{t('auth.firstName')}</Label><Input value={me.prenom || ''} onChange={(e) => setMe({ ...me, prenom: e.target.value })} data-testid="input-prof-prenom"/></div>
          <div><Label>{t('auth.lastName')}</Label><Input value={me.nom || ''} onChange={(e) => setMe({ ...me, nom: e.target.value })} data-testid="input-prof-nom"/></div>
          <div><Label>{t('auth.email')}</Label><Input value={me.email || ''} disabled/></div>
          <div><Label>{t('auth.phone')}</Label><Input value={me.telephone || ''} onChange={(e) => setMe({ ...me, telephone: e.target.value })} data-testid="input-prof-tel"/></div>
          <div><Label>{t('auth.streetNumber')}</Label><Input value={me.adresse_numero || ''} onChange={(e) => setMe({ ...me, adresse_numero: e.target.value })}/></div>
          <div><Label>{t('auth.streetName')}</Label><Input value={me.adresse_rue || ''} onChange={(e) => setMe({ ...me, adresse_rue: e.target.value })}/></div>
          <div><Label>{t('auth.postalCode')}</Label><Input value={me.adresse_code_postal || ''} onChange={(e) => setMe({ ...me, adresse_code_postal: e.target.value })}/></div>
          <div><Label>{t('auth.city')}</Label><Input value={me.adresse_ville || ''} onChange={(e) => setMe({ ...me, adresse_ville: e.target.value })}/></div>
        </div>
        <Button onClick={save} className="mt-5 bg-blue-700" data-testid="btn-save-profile"><Save className="w-4 h-4 mr-1"/> {t('common.save')}</Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2"><Lock className="w-5 h-5"/> {t('profile.security')}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div><Label>{t('profile.currentPassword')}</Label><Input type="password" value={pwd.old_password} onChange={(e) => setPwd({ ...pwd, old_password: e.target.value })}/></div>
          <div><Label>{t('profile.newPassword')}</Label><Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}/></div>
          <div><Label>{t('profile.confirmNewPassword')}</Label><Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}/></div>
        </div>
        <Button onClick={change} className="mt-4 bg-blue-700" data-testid="btn-change-password">{t('profile.changePassword')}</Button>
        <div className="mt-6 flex items-center justify-between p-4 rounded-lg bg-slate-50">
          <div><div className="font-medium">{t('profile.twoFactor')}</div><div className="text-xs text-slate-500">{t('profile.twoFactorDesc')}</div></div>
          <Switch checked={me.otp_enabled !== false} disabled/>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-semibold text-lg mb-4">{t('profile.documents')}</h2>
        <Button variant="outline" onClick={downloadRib} data-testid="btn-download-rib-profile"><Download className="w-4 h-4 mr-1"/> {t('dashboard.downloadMyRib')}</Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2"><History className="w-5 h-5"/> {t('profile.loginHistory')}</h2>
        <div className="divide-y">
          {logs.slice(0, 12).map((l) => (
            <div key={l.id} className="py-2 flex justify-between items-center text-sm">
              <div>
                <div className="font-medium">{new Date(l.created_at).toLocaleString('fr-FR')}</div>
                <div className="text-xs text-slate-500">{l.ip} • {(l.user_agent || '').slice(0, 60)}...</div>
              </div>
              <span className={`text-xs px-2 py-1 rounded ${l.statut === 'succes' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{l.statut}</span>
            </div>
          ))}
          {logs.length === 0 && <div className="text-slate-500 text-sm">{t('common.noResults')}</div>}
        </div>
      </div>
    </div>
  );
}
